import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  CreditCard, 
  Euro, 
  Clock, 
  CheckCircle,
  XCircle,
  FileText,
  Download,
  Loader2,
  Send,
  RotateCcw,
  Banknote,
  Eye
} from 'lucide-react';
import { exportDataset } from '../../lib/exportData';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente facture', class: 'bg-amber-100 text-amber-700', icon: Clock },
  invoice_uploaded: { label: 'Facture a valider', class: 'bg-blue-100 text-blue-700', icon: FileText },
  invoice_rejected: { label: 'Renvoyee', class: 'bg-red-100 text-red-700', icon: XCircle },
  valide_30j: { label: 'Valide - 30j', class: 'bg-purple-100 text-purple-700', icon: Clock },
  payment_pending: { label: 'En attente paiement', class: 'bg-cyan-100 text-cyan-700', icon: Clock },
  en_cours_paiement: { label: 'En cours paiement', class: 'bg-indigo-100 text-indigo-700', icon: Euro },
  paid: { label: 'Paye', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

export const AdminPayments = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await fetch(`${API}/admin/commission-invoices`, { credentials: 'include' });
      if (response.ok) {
        setCommissions(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateInvoice = async (commissionId) => {
    setActionLoading(commissionId);
    try {
      const response = await fetch(`${API}/admin/commission-invoices/${commissionId}/validate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Facture validee - En attente 30 jours');
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectDialog = (commission) => {
    setSelectedCommission(commission);
    setRejectComment('');
    setIsRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez indiquer le motif du renvoi');
      return;
    }

    setActionLoading(selectedCommission.commission_id);
    try {
      const formData = new FormData();
      formData.append('reason', rejectComment);

      const response = await fetch(`${API}/admin/commission-invoices/${selectedCommission.commission_id}/reject`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        toast.success('Facture renvoyee avec commentaire');
        setIsRejectOpen(false);
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const moveToPayment = async (commissionId) => {
    setActionLoading(commissionId);
    try {
      const response = await fetch(`${API}/admin/commission-invoices/${commissionId}/move-to-payment`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Commission en cours de paiement');
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const markPaid = async (commissionId) => {
    setActionLoading(commissionId);
    try {
      const response = await fetch(`${API}/admin/commission-invoices/${commissionId}/mark-paid`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Commission marquee comme payee');
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const downloadInvoice = async (commissionId, filename) => {
    setDownloading(commissionId);
    try {
      const response = await fetch(`${API}/commissions/${commissionId}/download-invoice`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'facture.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  // Calculate stats
  const toValidate = commissions.filter(c => c.status === 'invoice_uploaded').length;
  const waiting30d = commissions.filter(c => c.status === 'valide_30j').length;
  const inPayment = commissions.filter(c => ['en_cours_paiement', 'payment_pending'].includes(c.status)).length;
  const paidCount = commissions.filter(c => c.status === 'paid').length;

  // Filter commissions
  const filteredCommissions = filterStatus === 'all' 
    ? commissions 
    : commissions.filter(c => c.status === filterStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="admin-payments-title">
            Paiement des Commissions AC
          </h1>
          <p className="text-slate-500 mt-1">Gerez les factures et paiements des commerciaux</p>
        </div>
        <Button variant="outline" onClick={() => exportDataset('commission-invoices', 'paiements_AC')} data-testid="export-payments-btn">
          <Download className="w-4 h-4 mr-2" /> Exporter (CSV)
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterStatus === 'invoice_uploaded' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'invoice_uploaded' ? 'all' : 'invoice_uploaded')}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">A valider</p>
                <p className="text-2xl font-bold text-blue-600">{toValidate}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterStatus === 'valide_30j' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'valide_30j' ? 'all' : 'valide_30j')}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En attente 30j</p>
                <p className="text-2xl font-bold text-purple-600">{waiting30d}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterStatus === 'en_cours_paiement' ? 'ring-2 ring-indigo-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'en_cours_paiement' ? 'all' : 'en_cours_paiement')}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-indigo-100">
                <Euro className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En cours paiement</p>
                <p className="text-2xl font-bold text-indigo-600">{inPayment}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterStatus === 'paid' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'paid' ? 'all' : 'paid')}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100">
                <Banknote className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Payees</p>
                <p className="text-2xl font-bold text-emerald-600">{paidCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm" data-testid="payments-table">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Commissions AC
            </CardTitle>
            <CardDescription>
              {filterStatus === 'all' ? 'Toutes les commissions' : `Filtre: ${statusConfig[filterStatus]?.label}`}
            </CardDescription>
          </div>
          {filterStatus !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setFilterStatus('all')}>
              Voir tout
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Commercial</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Facture</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredCommissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Aucune commission
                  </TableCell>
                </TableRow>
              ) : (
                filteredCommissions.map((comm) => {
                  const StatusIcon = statusConfig[comm.status]?.icon || Clock;
                  const isLoading = actionLoading === comm.commission_id;
                  
                  return (
                    <TableRow key={comm.commission_id} className="hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{comm.commercial_name}</p>
                          <p className="text-xs text-slate-500">{comm.commercial_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{comm.client_name || '-'}</TableCell>
                      <TableCell className="font-bold text-emerald-600">
                        {comm.amount?.toLocaleString('fr-FR')} €
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[comm.status]?.class || 'bg-slate-100'} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[comm.status]?.label || comm.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {comm.invoice_filename ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(comm.commission_id, comm.invoice_filename)}
                            disabled={downloading === comm.commission_id}
                          >
                            {downloading === comm.commission_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Voir
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* Validate invoice button */}
                          {comm.status === 'invoice_uploaded' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => validateInvoice(comm.commission_id)}
                                disabled={isLoading}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                Valider
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRejectDialog(comm)}
                                className="text-amber-600 border-amber-300"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Renvoyer
                              </Button>
                            </>
                          )}
                          
                          {/* Move to payment button */}
                          {comm.status === 'valide_30j' && (
                            <Button
                              size="sm"
                              onClick={() => moveToPayment(comm.commission_id)}
                              disabled={isLoading}
                              className="bg-indigo-600 hover:bg-indigo-700"
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Euro className="w-4 h-4 mr-1" />}
                              En cours paiement
                            </Button>
                          )}
                          
                          {/* Mark as paid button */}
                          {['en_cours_paiement', 'payment_pending'].includes(comm.status) && (
                            <Button
                              size="sm"
                              onClick={() => markPaid(comm.commission_id)}
                              disabled={isLoading}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4 mr-1" />}
                              Marquer paye
                            </Button>
                          )}
                          
                          {comm.status === 'paid' && (
                            <span className="text-emerald-600 font-medium text-sm">Complete</span>
                          )}
                          
                          {comm.status === 'pending' && (
                            <span className="text-slate-400 text-sm">En attente facture AC</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renvoyer la facture</DialogTitle>
            <DialogDescription>
              Indiquez le motif du renvoi pour {selectedCommission?.commercial_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motif du renvoi *</Label>
              <Textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Veuillez corriger..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleReject} 
              disabled={actionLoading || !rejectComment.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Renvoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
